/**
 * Registration & login hardening (V-01, V-02).
 *
 * - Normalizes email addresses (strips +alias tags) before submit
 * - Client-side rate limiting on auth forms
 * - Honeypot bot trap
 * - Optional Consent API integration for email verification flow
 */
(function () {
  'use strict';

  var MAX_ATTEMPTS = 5;
  var WINDOW_MS = 15 * 60 * 1000;

  var LOCALE = (document.documentElement.lang || 'en').slice(0, 2);
  var MSG = {
    en: {
      rate_limited:
        'Too many attempts. Please wait 15 minutes before trying again.',
      email_exists:
        'An account with this email address already exists. Please sign in instead.',
      bot_detected: 'Unable to submit the form. Please refresh and try again.',
      check_email:
        'Verification email sent! Please check your inbox to confirm your account.',
      connection_error: 'Connection error. Please try again.',
      register_failed: 'Registration failed. Please try again.',
    },
    it: {
      rate_limited:
        'Troppi tentativi. Attendi 15 minuti prima di riprovare.',
      email_exists:
        'Esiste già un account con questo indirizzo email. Accedi invece.',
      bot_detected:
        'Impossibile inviare il modulo. Aggiorna la pagina e riprova.',
      check_email:
        'Email di verifica inviata! Controlla la tua casella di posta per confermare.',
      connection_error: 'Errore di connessione. Riprova.',
      register_failed: 'Registrazione fallita. Riprova.',
    },
  };

  function t(key) {
    var bucket = MSG[LOCALE] || MSG.en;
    return bucket[key] || MSG.en[key] || key;
  }

  /**
   * Strip plus-addressing tags (user+tag@domain → user@domain).
   * Prevents unlimited account creation via email aliasing (V-02).
   */
  function normalizeBaseEmail(email) {
    if (!email || typeof email !== 'string') return '';
    var trimmed = email.trim().toLowerCase();
    var atIndex = trimmed.lastIndexOf('@');
    if (atIndex <= 0) return trimmed;
    var local = trimmed.slice(0, atIndex);
    var domain = trimmed.slice(atIndex + 1);
    var plusIndex = local.indexOf('+');
    if (plusIndex !== -1) {
      local = local.slice(0, plusIndex);
    }
    return local + '@' + domain;
  }

  function checkRateLimit(action) {
    var key = 'cf_auth_' + action;
    var now = Date.now();
    try {
      var raw = sessionStorage.getItem(key);
      var data = raw ? JSON.parse(raw) : { attempts: [], blockedUntil: 0 };
      if (data.blockedUntil && data.blockedUntil > now) {
        return false;
      }
      data.attempts = (data.attempts || []).filter(function (ts) {
        return now - ts < WINDOW_MS;
      });
      if (data.attempts.length >= MAX_ATTEMPTS) {
        data.blockedUntil = now + WINDOW_MS;
        sessionStorage.setItem(key, JSON.stringify(data));
        return false;
      }
      data.attempts.push(now);
      sessionStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (e) {
      return true;
    }
  }

  function showSecurityError(containerId, message) {
    var el = document.getElementById(containerId);
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    el.setAttribute('role', 'alert');
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function clearSecurityError(containerId) {
    var el = document.getElementById(containerId);
    if (!el) return;
    el.textContent = '';
    el.classList.remove('is-visible');
    el.removeAttribute('role');
  }

  function showEmailSentOverlay(message) {
    var overlay = document.getElementById('register-email-sent');
    var msgEl = document.getElementById('register-email-sent-msg');
    if (!overlay) return;
    if (msgEl) msgEl.textContent = message;
    overlay.classList.add('is-visible');
    var form = document.querySelector('form[action*="/account"]');
    if (form) form.style.display = 'none';
  }

  function apiFetch(apiUrl, apiKey, path, options) {
    var headers = Object.assign(
      { 'X-API-Key': apiKey },
      (options && options.headers) || {}
    );
    return fetch(apiUrl + path, Object.assign({}, options, { headers: headers }));
  }

  function checkEmailViaApi(apiUrl, apiKey, shopId, email) {
    return apiFetch(
      apiUrl,
      apiKey,
      '/api/consent/check-email?email=' +
        encodeURIComponent(email) +
        '&shop_id=' +
        encodeURIComponent(shopId),
      { method: 'GET' }
    ).then(function (res) {
      return res.json();
    });
  }

  function initRegisterForm() {
    var configEl = document.getElementById('register-consent-config');
    var registerForm = document.querySelector('form[action*="/account"]');
    if (!registerForm) return;

    var API_URL = configEl ? configEl.getAttribute('data-api-url') || '' : '';
    var API_KEY = configEl ? configEl.getAttribute('data-api-key') || '' : '';
    var SHOP_ID = configEl
      ? configEl.getAttribute('data-shop-id') || ''
      : '';
    var useApi = Boolean(API_URL && API_KEY && SHOP_ID);

    registerForm.addEventListener('submit', function (e) {
      clearSecurityError('RegisterForm-security-error');

      var honeypot = document.getElementById('RegisterForm-hp');
      if (honeypot && honeypot.value) {
        e.preventDefault();
        showSecurityError('RegisterForm-security-error', t('bot_detected'));
        return;
      }

      if (!checkRateLimit('register')) {
        e.preventDefault();
        showSecurityError('RegisterForm-security-error', t('rate_limited'));
        return;
      }

      var emailInput = document.getElementById('RegisterForm-email');
      var rawEmail = emailInput ? emailInput.value.trim() : '';
      if (!rawEmail) return;

      var normalizedEmail = normalizeBaseEmail(rawEmail);
      if (emailInput) {
        emailInput.value = normalizedEmail;
      }

      if (!useApi) return;

      e.preventDefault();

      var submitBtn = registerForm.querySelector(
        'button[type="submit"], .btn-primary'
      );
      if (submitBtn) submitBtn.classList.add('is-loading');

      var hiddenTagsInput = document.querySelector('.register-form__tag');
      var hiddenMarketingInput = document.getElementById(
        'register-form__accepts_marketing'
      );
      var tags = hiddenTagsInput
        ? hiddenTagsInput.value.split(',').filter(Boolean)
        : [];
      var firstNameInput = document.getElementById('RegisterForm-FirstName');
      var lastNameInput = document.getElementById('RegisterForm-LastName');
      var passwordInput = document.getElementById('RegisterForm-password');

      checkEmailViaApi(API_URL, API_KEY, SHOP_ID, normalizedEmail)
        .then(function (data) {
          if (data && data.exists) {
            if (submitBtn) submitBtn.classList.remove('is-loading');
            showSecurityError(
              'RegisterForm-security-error',
              t('email_exists')
            );
            return null;
          }

          return apiFetch(API_URL, API_KEY, '/api/consent/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              shop_id: SHOP_ID,
              email: normalizedEmail,
              tags: tags,
              first_name: firstNameInput ? firstNameInput.value.trim() : '',
              last_name: lastNameInput ? lastNameInput.value.trim() : '',
              source: 'register',
              locale: LOCALE,
              password_method: 'now',
              password: passwordInput ? passwordInput.value : '',
              accepts_marketing:
                hiddenMarketingInput &&
                hiddenMarketingInput.value === 'true',
            }),
          }).then(function (res) {
            return res.json().then(function (body) {
              return { ok: res.ok, data: body };
            });
          });
        })
        .then(function (result) {
          if (!result) return;
          if (submitBtn) submitBtn.classList.remove('is-loading');
          if (!result.ok) {
            showSecurityError(
              'RegisterForm-security-error',
              (result.data && result.data.error) || t('register_failed')
            );
            return;
          }
          showEmailSentOverlay(
            (result.data && result.data.message) || t('check_email')
          );
        })
        .catch(function () {
          if (submitBtn) submitBtn.classList.remove('is-loading');
          showSecurityError(
            'RegisterForm-security-error',
            t('connection_error')
          );
        });
    });
  }

  function initLoginForm() {
    var loginForm = document.querySelector('form[action*="/login"]');
    if (!loginForm) return;

    loginForm.addEventListener('submit', function (e) {
      clearSecurityError('LoginForm-security-error');

      var honeypot = document.getElementById('LoginForm-hp');
      if (honeypot && honeypot.value) {
        e.preventDefault();
        showSecurityError('LoginForm-security-error', t('bot_detected'));
        return;
      }

      if (!checkRateLimit('login')) {
        e.preventDefault();
        showSecurityError('LoginForm-security-error', t('rate_limited'));
        return;
      }

      var emailInput = document.getElementById('CustomerEmail');
      if (emailInput && emailInput.value) {
        emailInput.value = normalizeBaseEmail(emailInput.value);
      }
    });
  }

  window.RegisterSecurity = {
    normalizeBaseEmail: normalizeBaseEmail,
    checkRateLimit: checkRateLimit,
  };

  document.addEventListener('DOMContentLoaded', function () {
    initRegisterForm();
    initLoginForm();
  });
})();
